import type { RefObject } from "react";
import { useEffect, useRef } from "react";
import * as Styled from "./style";
import { useRouter } from "next/router";
import { POPULAR_CATEGORIES } from "~/components/blocks/layout/global-navi/const/popular-categories";
import { ListItem } from "~/components/blocks/layout/global-navi/mobile/right/search-form/history-list/list-item";
import type { SearchHistoryType } from "~/components/blocks/layout/global-navi/util/search-history-manipulator";
import type { Hrefs } from "~/const/hrefs";
import { PATHS } from "~/const/paths";

export type HistoryListProps = {
  /** 検索フォームのRef Object */
  searchInputRef: RefObject<HTMLLabelElement>;
  /** 検索履歴リストの表示状態 */
  isShowHistoryList: boolean;
  /** 検索履歴の配列 */
  searchHistories: SearchHistoryType[];
  /** 視聴制限がされていないかどうか */
  isViewableUnrestricted: boolean;
  /** 検索フォーム非表示処理 */
  onHideSearch: () => void;
  /** 検索履歴除去処理 */
  onRemoveHistory: (idx: number) => void;
  /** 検索履歴リストの非表示関数 */
  onHideHistoryList: () => void;
  /** 検索履歴押下時キーワード更新 */
  onUpdateKeyword: (history: string) => void;
  /** 検索履歴効果時の検索を実行する関数 */
  pushToSearch: (arg: SearchHistoryType) => void;
};

export const HistoryList = (props: HistoryListProps) => {
  const {
    searchHistories,
    isShowHistoryList,
    isViewableUnrestricted,
    onHideSearch,
    onRemoveHistory,
    onHideHistoryList,
    onUpdateKeyword,
    pushToSearch,
  } = props;

  const router = useRouter();
  const historyListRef = useRef<HTMLDivElement>(null);

  const filteredSearchHistories = isViewableUnrestricted
    ? searchHistories
    : searchHistories.filter((searchHistory) => searchHistory.type !== "gravure");

  /** 検索履歴一覧の高さ取得 */
  const windowLock = (event: Event) => {
    event.preventDefault();
  };

  const disableScroll = () => {
    if (!isShowHistoryList) return;
    window.addEventListener("touchmove", windowLock, { passive: false });
    window.addEventListener("wheel", windowLock, { passive: false });
    return () => {
      window.removeEventListener("touchmove", windowLock);
      window.removeEventListener("wheel", windowLock);
    };
  };
  useEffect(disableScroll, [isShowHistoryList]);

  const onNavigate = async (href: Hrefs) => {
    onHideSearch();
    void router.push(href);
  };

  return (
    <Styled.DivContainer ref={historyListRef} isShowElement={isShowHistoryList}>
      {filteredSearchHistories.length !== 0 && (
        <>
          <Styled.DivHeading>検索履歴</Styled.DivHeading>
          <Styled.ULContainer>
            {filteredSearchHistories.map((history, index) => (
              <ListItem
                isHistory
                key={`history-${index}`}
                name={history.keyword}
                isGravure={history.type === "gravure"}
                onNavigate={async () => {
                  onHideHistoryList();
                  onUpdateKeyword(history.keyword);
                  pushToSearch({ keyword: history.keyword, type: history.type });
                }}
                onRemoveHistory={() => onRemoveHistory(index)}
              />
            ))}
          </Styled.ULContainer>
        </>
      )}
      <Styled.DivHeading>ランキング</Styled.DivHeading>
      <Styled.ULContainer>
        <ListItem
          name="ビデオ総合ランキング"
          onNavigate={() => onNavigate({ pathname: PATHS.VOD_RANKING, query: { category: "" } })}
        />
      </Styled.ULContainer>
      <Styled.DivHeading>ビデオ人気カテゴリ</Styled.DivHeading>
      <Styled.ULContainer>
        {POPULAR_CATEGORIES.map(({ name, queryParam }, index) => (
          <ListItem
            key={`video-${index}`}
            name={name}
            onNavigate={() => onNavigate({ pathname: PATHS.VOD, query: { id: queryParam } })}
          />
        ))}
      </Styled.ULContainer>
    </Styled.DivContainer>
  );
};
